import FileContentUploaderButton from 'components/FileContentUploaderButton/ContentUploader';
import FileDownloadDropdown from 'components/FileDownloadDropdown/FileDownloadDropdown';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  showFailureToast,
  showInfoToast,
  showSuccessToast,
} from 'lib/ToastHelper';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Row, Col, Input } from 'reactstrap';
import { companyIdAtom, HostActiveStatusFilterType } from 'views/state';

import DashboardActions from './DashboardActions';
import { getExportCsvUrlParams } from './helper';
import {
  checkedListAtom,
  orderingAtom,
  paginationConfigAtom,
  scannerTypeFilterAtom,
  searchPhraseFilterAtom,
  selectedEnpointsFilterAtom,
} from './state';
import { epRefreshAtom } from '../state';

const ScannerTypeFilter = [
  { value: '', label: 'All' },
  { value: 'Internal', label: 'Internal' },
  { value: 'External', label: 'External' },
  { value: 'EC2', label: 'AWS' },
];

const EndpointsFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'aging', label: 'In Aging Out Process' },
  { value: 'deleted', label: 'Deleted (Aged Out or Manual)' },
];

const colorStyles = {
  control: (styles) => ({ ...styles, backgroundColor: 'white' }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    // const color = chroma(data.color);
    return {
      ...styles,
      textAlign: 'left',
    };
  },
};

const Filters = () => {
  // PRESERVED STATE
  const companyId = useAtomValue(companyIdAtom);
  const [scannerTypeFilter, setScannerTypeFilter] = useAtom(
    scannerTypeFilterAtom,
  );
  const [selectedEndpointsFilter, setSelectedEndpointsFilter] = useAtom(
    selectedEnpointsFilterAtom,
  );
  const [searchPhraseFilter, setSearchPhraseFilter] = useAtom(
    searchPhraseFilterAtom,
  );
  const [checkedList, setCheckedList] = useAtom(checkedListAtom);
  const ordering = useAtomValue(orderingAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const setEPRefresh = useSetAtom(epRefreshAtom);

  // NORMAL STATE
  const [searchPhrase2, setSearchPhrase2] = useState('');

  const searchField = useRef<any>();

  useEffect(() => {
    if (searchPhraseFilter !== searchPhrase2)
      setSearchPhrase2(searchPhraseFilter);
  }, [searchPhraseFilter]);

  const debouncedSearch = React.useRef(
    debounce(async (newSearchPhrase, oldSearchPhrase, setSearchPhrase) => {
      if (newSearchPhrase !== oldSearchPhrase) {
        setSearchPhrase(newSearchPhrase);
        setCheckedList([]);
      }
    }, 1000),
  ).current;

  React.useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSuccessBulkAlias = (response) => {
    setEPRefresh(Math.random().toString());
    if (response['endpoint_alias_csv'] === response['endpoint_alias_updated'])
      showSuccessToast(response['message']);
    else showInfoToast(response['message']);
  };

  return (
    <Row className="text-left">
      <Col md="3" sm="12">
        <div className="d-flex align-items-center justify-content-end">
          <DashboardActions />
          <FileDownloadDropdown
            buttonId="ep_export_all"
            iconClass="fa fa-file-export"
            tooltip="Export CSV data for Endpoints"
            method="GET"
            onSuccessCallback={() =>
              showSuccessToast('File download completed.')
            }
            onFailureCallback={(message) => showFailureToast(message)}
            config={[
              {
                url: 'endpoints/export-csv',
                label: 'Export Endpoints',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_endpoints.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  scannerTypeFilter,
                  searchPhraseFilter,
                  checkedList,
                  selectedEndpointsFilter,
                ),
              },
              {
                url: 'endpoints/export-csv',
                label: 'Export Aliases',
                fileName: `${localStorage
                  .getItem('currentCompany')
                  ?.toLowerCase()}_endpoint_aliases.csv`,
                urlParams: getExportCsvUrlParams(
                  companyId,
                  ordering,
                  scannerTypeFilter,
                  searchPhraseFilter,
                  checkedList,
                  selectedEndpointsFilter,
                  'alias',
                ),
              },
            ]}
          />
          <FileContentUploaderButton
            buttonId="bulkaliasButton"
            iconClass="fa fa-file-import"
            tooltip="Import/Update Endpoint Alias In Bulk"
            method="POST"
            uploadUrl="endpoints/alias/bulk/"
            allowedExtensions={['csv']}
            data={{ company_id: companyId }}
            onSuccessCallback={onSuccessBulkAlias}
            onFailureCallback={(message) => showFailureToast(message)}
            disabled={selectedEndpointsFilter === 'deleted'}
          />
        </div>
      </Col>
      <Col md="2" sm="12" className="text-left" style={{ paddingRight: '0px' }}>
        {/* <Label>Type</Label> */}
        <Select
          id="ScannerTypeFilterDropdown"
          className="custom-360-dropdown"
          value={ScannerTypeFilter.find((x) => x.value === scannerTypeFilter)}
          onChange={(e) => {
            if (e) {
              setPaginationConfig({ ...paginationConfig, page: 1 });

              setScannerTypeFilter(e.value);
              setCheckedList([]);
            }
          }}
          styles={colorStyles}
          options={ScannerTypeFilter}
        />
      </Col>
      <Col md="2" sm="12" className="text-left" style={{ paddingRight: '0px' }}>
        <Select
          id="EndpointsFilterOptionsDropdown"
          className="custom-360-dropdown"
          value={EndpointsFilterOptions.find(
            (x) => x.value === selectedEndpointsFilter,
          )}
          onChange={(e) => {
            if (e) {
              setPaginationConfig({ ...paginationConfig, page: 1 });
              setSelectedEndpointsFilter(e.value as HostActiveStatusFilterType);
              setCheckedList([]);
            }
          }}
          styles={colorStyles}
          defaultValue={EndpointsFilterOptions[0]}
          options={EndpointsFilterOptions}
        />
      </Col>
      <Col md="5" sm="12" className="text-left">
        <Input
          ref={searchField}
          id="EndpointsSearch"
          type="text"
          list="data"
          value={searchPhrase2}
          placeholder="Search Endpoints"
          onChange={(e) => {
            setSearchPhrase2(e.target.value);
            debouncedSearch(
              e.target.value,
              searchPhraseFilter,
              setSearchPhraseFilter,
            );
          }}
        />
      </Col>
    </Row>
  );
};

export default Filters;
